<template>
  <div>
    <validation-observer ref="form" novalidate>
      <div class="row">
        <div class="col-12 col-lg-6">
          <section-title
            container-class="tag-badge-main-info mt-4 mb-4"
            content="VII. Pareo Ocupacional con la Meta Vocacional, Intereses"
            icon="fas fa-clipboard-list-check"
          />
        </div>
        <div
          class="col-12 col-lg-6 d-flex justify-content-lg-end align-items-center"
        >
          <action-button
            :i="allExpanded ? 'fal fa-compress-alt' : 'fal fa-expand-alt'"
            :text="allExpanded ? 'Colapsar Todo' : 'Expandir Todo'"
            variant="outline-secondary expand-all-btn"
            @click="toggleExpandAll(!allExpanded)"
          />
        </div>
      </div>
      <div class="mx-3">
        <custom-collapse id="riasec-and-interest-collapse">
          <custom-collapse-item
            v-for="item in riasecAndInterestCategories"
            :key="`section-${item.id}`"
            ref="riasecCollapseItems"
            auto
            class="my-3 collapse-item section bg-gray"
            expanded-icon="fas fa-chevron-circle-right color-gray-7"
            minimized-icon="fas fa-chevron-circle-down color-warning"
            parent-id="riasec-and-interest-collapse"
            read-only-icon="fas fa-plus-circle color-gray-2"
            start-expanded
            :title="item.name"
            @expandButtonClick="syncExpandButtonState"
          >
            <div class="bg-white p-3 rounded">
              <div
                v-for="area in item.areas"
                :key="`section-area-${area.area.id}`"
              >
                <answer
                  :is-read-only="isReadOnly"
                  :value="area"
                  @input="onAnswer(item, $event)"
                />
              </div>
            </div>
          </custom-collapse-item>
        </custom-collapse>
      </div>
      <section-title
        content="Factores ocupacionales relacionados al interés del evaluado"
      />
      <div class="mx-3">
        <custom-collapse id="factors-collapse">
          <ged-occupational-factors
            ref="gedCollapse"
            v-model="model.gedConfigurationDto"
            :is-read-only="isReadOnly"
            @expandButtonClick="syncExpandButtonState"
          />

          <svp-occupational-factors
            ref="svpCollapse"
            v-model="model.svpConfigurationDto"
            :is-read-only="isReadOnly"
            @expandButtonClick="syncExpandButtonState"
          />

          <aptitude-occupational-factors
            ref="aptitudeCollapse"
            v-model="model.aptitudeConfigurationDto"
            :is-read-only="isReadOnly"
            @expandButtonClick="syncExpandButtonState"
          />

          <goe-occupational-factors
            ref="goeCollapse"
            v-model="model.goeConfigurationDto"
            :is-read-only="isReadOnly"
            @expandButtonClick="syncExpandButtonState"
          />
          <custom-collapse-item
            v-for="item in evaluatedCategories"
            :key="`factors-section-${item.id}`"
            ref="evaluatedCollapseItems"
            auto
            class="my-3 collapse-item section bg-gray"
            expanded-icon="fas fa-chevron-circle-right color-gray-7"
            minimized-icon="fas fa-chevron-circle-down color-warning"
            parent-id="factors-collapse"
            read-only-icon="fas fa-plus-circle color-gray-2"
            start-expanded
            :title="item.name"
            @expandButtonClick="syncExpandButtonState"
          >
            <div v-if="item.leyends?.length" class="legend-bar mb-3">
              <span
                v-for="legend in item.leyends"
                :key="legend.id"
                class="legend-bar__item"
              >
                {{ legend.name }}
              </span>
            </div>
            <div class="bg-white p-3 rounded">
              <div class="row">
                <div
                  v-for="area in item.areas"
                  :key="`section-area-${area.area.id}`"
                  :class="[isHalf(item) ? 'col-md-6 col-12' : 'col-12']"
                >
                  <evaluated-area-answer
                    :answer-options="item.questions"
                    :is-half="isHalf(item)"
                    :is-read-only="isReadOnly"
                    :state-options="item.states"
                    :value="area"
                    :yes-no-options="item.options"
                    @input="onAnswer(item, $event)"
                  />
                </div>
              </div>
              <div v-if="hasCategoryComment(item)">
                <label>Observaciones:</label>
                <custom-text-area
                  :count-classes="['d-block', 'small-counter', 'text-right']"
                  :disabled="isReadOnly"
                  :maxlength="5000"
                  placeholder="Comentario"
                  show-char-count
                  :value="categoryComment(item)"
                  @input="setCategoryComment(item, $event)"
                />
              </div>
            </div>
          </custom-collapse-item>
        </custom-collapse>
      </div>
    </validation-observer>
  </div>
</template>

<script>
//mixins
import alertMixin from '@/mixins/alert.mixin.js';

//utils
import { copy } from '@/utils/objUtils';

//components
import CustomCollapse from '@/components/common/CustomCollapse.vue';
import CustomCollapseItem from '@/components/common/CustomCollapseItem.vue';
import SectionTitle from '@/shared/components/SectionTitle.vue';
import CustomTextArea from 'CustomTextArea';

//local-components
import Answer from './components/AreaAnswer.vue';
import EvaluatedAreaAnswer from './components/EvaluatedAreaAnswer.vue';
import GedOccupationalFactors from './components/GedOccupationalFactors.vue';
import SvpOccupationalFactors from './components/SvpOccupationalFactors.vue';
import AptitudeOccupationalFactors from './components/AptitudeOccupationalFactors.vue';
import GoeOccupationalFactors from './components/GoeOccupationalFactors.vue';

//services
import { getSeven } from '@/modules/vocational-evaluation/api.js';

//constants
import Categories from '@/utils/constants/vocationalEvaluationInstrumentAreaCategory.js';

export default {
  name: 'VocationalEducationPartSeven',
  components: {
    CustomCollapse,
    CustomCollapseItem,
    SectionTitle,
    CustomTextArea,
    Answer,
    EvaluatedAreaAnswer,
    GedOccupationalFactors,
    SvpOccupationalFactors,
    AptitudeOccupationalFactors,
    GoeOccupationalFactors,
  },
  mixins: [alertMixin],
  props: {
    id: {
      type: [Number, String],
      default: () => null,
    },
    appointmentId: {
      type: [Number, String],
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    value: {
      type: Object,
      required: true,
    },
    initialModel: {
      type: Object,
      required: false,
      default: () => null,
    },
    keepUpdates: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    isReadOnly: {
      type: Boolean,
      required: true,
      default: () => false,
    },
  },
  data() {
    return {
      allExpanded: true,
    };
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    categoryComment() {
      return (category) =>
        this.hasCategoryComment(category)
          ? category.areas.find((a) => ![null, undefined].includes(a.comment))
              ?.comment
          : null;
    },
    hasCategoryComment() {
      return (category) =>
        [
          Categories.Temperaments,
          Categories.EnvironmentalWorkConditions,
          Categories.PhysicalDemandsPhysicalMovements,
        ].includes(category.id);
    },
    isHalf() {
      return (category) =>
        [
          Categories.Temperaments,
          Categories.EnvironmentalWorkConditions,
          Categories.PhysicalDemandsForce,
          Categories.PhysicalDemandsPhysicalMovements,
        ].includes(category.id);
    },
    filled() {
      return this.initialModel?.filled ?? false;
    },
    riasecAndInterestCategories() {
      return (this.model.categories ?? [])?.filter((c) =>
        [Categories.Riasec, Categories.InterestAreas].includes(c.id)
      );
    },
    evaluatedCategories() {
      return (this.model.categories ?? [])?.filter(
        (c) => ![Categories.Riasec, Categories.InterestAreas].includes(c.id)
      );
    },
  },
  beforeDestroy() {
    this.$emit('update:initialModel', this.model);
  },
  async mounted() {
    this.$emit('update:disabled', true);
    await this.get();
    this.$emit('update:disabled', false);
  },
  methods: {
    toggleExpandAll() {
      this.allExpanded = !this.allExpanded;

      this.expandRiasecAndInterest(this.allExpanded);
      this.expandOccupationalFactors(this.allExpanded);
    },

    setCollapseItemsExpanded(items, expand) {
      const list = Array.isArray(items) ? items : [items];

      list.filter(Boolean).forEach((item) => {
        item.isExpended = expand;
      });
    },

    expandRiasecAndInterest(expand) {
      this.setCollapseItemsExpanded(this.$refs.riasecCollapseItems, expand);
    },

    expandOccupationalFactors(expand) {
      this.$refs.gedCollapse?.setExpanded(expand);
      this.$refs.svpCollapse?.setExpanded(expand);
      this.$refs.aptitudeCollapse?.setExpanded(expand);
      this.$refs.goeCollapse?.setExpanded(expand);

      this.setCollapseItemsExpanded(this.$refs.evaluatedCollapseItems, expand);
    },
    syncExpandButtonState() {
      const riasecItems = this.$refs.riasecCollapseItems || [];
      const evaluatedItems = this.$refs.evaluatedCollapseItems || [];

      const allItems = [
        ...riasecItems,
        this.$refs.gedCollapse?.$refs?.collapseItem,
        this.$refs.svpCollapse?.$refs?.collapseItem,
        this.$refs.aptitudeCollapse?.$refs?.collapseItem,
        this.$refs.goeCollapse?.$refs?.collapseItem,
        ...evaluatedItems,
      ].filter(Boolean);

      this.allExpanded = allItems.some((item) => item.isExpended);
    },
    setModelFrom(value) {
      const data = copy(value);
      this.model = data;
      this.$emit('update:initialModel', data);
    },
    async get() {
      if (this.keepUpdates && this.filled) {
        this.setModelFrom(this.initialModel);
        return;
      }

      if (!this.appointmentId) return;
      const { data } = await getSeven({
        id: this.id,
        appointmentId: this.appointmentId,
      });
      this.setModelFrom({ ...data, filled: true });
    },
    async validate() {
      return await this.$refs.form.validate();
    },
    onAnswer(category, area) {
      const index = category.areas.findIndex((a) => a.area?.id === area.id);
      if (index !== -1) category.areas[index] = area;
    },
    setCategoryComment(category, value) {
      if (category.areas[0]) category.areas[0].comment = value;
    },
  },
};
</script>
<style scoped>
.collapse-item ::v-deep .card-header {
  padding: 0.75rem 1rem !important;
}

.collapse-item ::v-deep .tag-badge-pei {
  background: none;
}

.collapse-item.section ::v-deep .shadow2 {
  -webkit-box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
  box-shadow: none !important;
  background-color: #f7f7f7;
}
.collapse-item.section ::v-deep .card-header {
  border-radius: 0.25rem !important;
  border-bottom: 2px solid #e6e6e6;
}
.collapse-item.section ::v-deep .card-header .title-number-box {
  background-color: #2db782;
}

.collapse-item.invalid.section {
  border: 1px solid #ff0018 !important;
}
.collapse-item.invalid.section ::v-deep .card-header .title-number-box {
  background-color: #ff5b5c !important;
}
.legend-bar {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 0.6rem 1rem;
  border: 1px solid #e6e6e6;
  border-radius: 0.25rem;
  font-weight: 800;
  background: #fff;
  font-size: 0.85rem;
}

.legend-bar__item {
  flex: 1;
  text-align: center;
}

.collapse-item ::v-deep .color-warning {
  color: #ff5b5c !important;
}
::v-deep
  .custom-control-input:disabled:checked
  ~ .custom-control-label::before {
  background-color: #3f5b60 !important;
  border-color: #3f5b60 !important;
}
</style>
